import { createSlice } from "@reduxjs/toolkit";
import { v4 as uuid } from "uuid";
import { paymentApi } from "@/services/api/entities/payment";
import type { MaybeType } from "@/shared/types";
import type {
  AllTariffResponse,
  CalculatePricePayload,
  CalculatePriceResponse,
  PaymentUrlPayload,
  PaymentUrlResponse,
  SelectedTariffOption,
  Tariff,
} from "@/services/api/entities/payment/types";
import type { PaymentState, FlowType } from "@/features/payment-form/model/types";
import type { PayloadAction } from "@reduxjs/toolkit";

const initialState: PaymentState = {
  idempotenceKey: "",
  paymentOperationId: "",
  checkPaymentPolling: false,
  checkPaymentRetryCount: 0,
  flowType: null,
  selectedTariff: null,
  selectedTariffOptions: [],
  priceCalculation: null,
  paymentUrlHasError: false,
  paymentUrl: null,
};

export const paymentSlice = createSlice({
  name: "payments",
  initialState,
  reducers: {
    setIdempotenceKey(state, action: PayloadAction<string>) {
      state.idempotenceKey = action.payload;
    },
    setPaymentOperationId(state, action: PayloadAction<string>) {
      state.paymentOperationId = action.payload;
    },
    setCheckPaymentPolling(state, action: PayloadAction<boolean>) {
      state.checkPaymentPolling = action.payload;
    },
    incrementCheckPaymentRetryCount(state) {
      state.checkPaymentRetryCount += 1;
    },
    resetCheckPaymentRetryCount(state) {
      state.checkPaymentRetryCount = 0;
    },
    setFlowType(state, action: PayloadAction<FlowType>) {
      state.flowType = action.payload;
    },
    setSelectedTariff(state, action: PayloadAction<MaybeType<Tariff>>) {
      state.selectedTariff = action.payload;

      state.selectedTariffOptions =
        action.payload?.options.map((option) => ({
          optionId: option.id,
          optionItemId: option.items.length > 0 ? option.items[0].id : null,
        })) ?? [];
    },
    updateSelectedTariffOption(state, action: PayloadAction<SelectedTariffOption>) {
      const { optionId, optionItemId } = action.payload;
      const existingOption = state.selectedTariffOptions.find((opt) => opt.optionId === optionId);

      if (existingOption) {
        existingOption.optionItemId = optionItemId;
      } else {
        state.selectedTariffOptions.push({ optionId, optionItemId });
      }
    },
    setDiscountCode(state, action: PayloadAction<string>) {
      state.discountCode = action.payload;
    },
    resetPaymentUrlError(state) {
      state.paymentUrlHasError = false;
    },
    resetPaymentState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      paymentApi.endpoints.getAllTariffs.matchFulfilled,
      (state: PaymentState, action: PayloadAction<AllTariffResponse>) => {
        const selectedTariff = action.payload[0];

        const { options } = selectedTariff;

        const selectedTariffOptions = options.map((option) => {
          return {
            optionId: option.id,
            optionItemId: option.items.length > 0 ? option.items[0].id : null,
          };
        });

        state.selectedTariff = selectedTariff;
        state.selectedTariffOptions = selectedTariffOptions;
      },
    );

    builder.addMatcher(
      paymentApi.endpoints.getPriceCalculation.matchFulfilled,
      (state, action: PayloadAction<CalculatePriceResponse>) => {
        state.priceCalculation = action.payload;
        state.idempotenceKey = uuid();
      },
    );

    builder.addMatcher(paymentApi.endpoints.getPaymentUrl.matchRejected, (state) => {
      state.paymentUrlHasError = true;
    });
    builder.addMatcher(
      paymentApi.endpoints.getPaymentStatus.matchFulfilled,
      (state, action: PayloadAction<PaymentUrlResponse>) => {
        state.checkPaymentRetryCount += 1;
        state.paymentUrl = action.payload.paymentUrl;
      },
    );
  },
  selectors: {
    selectIdempotenceKey: (state: PaymentState) => state.idempotenceKey,
    selectPaymentOperationId: (state: PaymentState) => state.paymentOperationId,
    selectCheckPaymentPolling: (state: PaymentState) => state.checkPaymentPolling,
    selectPaymentRetryCount: (state: PaymentState) => state.checkPaymentRetryCount,
    selectFlowType: (state: PaymentState) => state.flowType,
    selectSelectedTariff: (state: PaymentState) => state.selectedTariff,
    selectSelectedTariffOptions: (state: PaymentState) => state.selectedTariffOptions,
    selectDiscountCode: (state: PaymentState) => state.discountCode,
    selectPriceCalculation: (state: PaymentState) => state.priceCalculation,
    selectPaymentUrlHasError: (state: PaymentState) => state.paymentUrlHasError,
    selectPaymentUrl: (state: PaymentState) => state.paymentUrl,
    selectCalculatePricePayload: (state: PaymentState): MaybeType<CalculatePricePayload> => {
      if (!state.selectedTariff) return null;

      const discount = state.discountCode ? { code: state.discountCode } : undefined;

      return {
        tariffId: state.selectedTariff.tariffPlanId,
        options: state.selectedTariffOptions.length > 0 ? state.selectedTariffOptions : undefined,
        discount,
      };
    },
    selectPaymentUrlPayload: (state: PaymentState): MaybeType<PaymentUrlPayload> => {
      if (!state.selectedTariff || !state.priceCalculation) return null;

      const discount = state.discountCode ? { code: state.discountCode } : undefined;

      return {
        tariffPlanId: state.selectedTariff.tariffPlanId,
        idempotenceKey: state.idempotenceKey || uuid(),
        redirectUrl: `${window.location.protocol}//${window.location.host}/auth/check-payment`,
        discount,
        options: state.selectedTariffOptions.length > 0 ? state.selectedTariffOptions : undefined,
        lastPrice: state.priceCalculation,
      };
    },
    selectShouldSkipPolling: (state: PaymentState) => {
      const { paymentOperationId, checkPaymentPolling, checkPaymentRetryCount } = state;

      return !paymentOperationId || !checkPaymentPolling || checkPaymentRetryCount > 5;
    },
  },
});

export const {
  actions: paymentActions,
  reducer: paymentReducer,
  selectors: paymentSelectors,
} = paymentSlice;
